import { useMutation, useQueryClient } from '@tanstack/react-query';

import { RepositoryShortTag } from '@/portainer/registry-management/models/repositoryTag';
import { findBestStep } from '@/portainer/registry-management/services/genericAsyncGenerator';
import { QueueState, RequestQueue } from '@/react/common/RequestQueue';
import axios from '@/portainer/services/axios/axios';
import { withError } from '@/react-tools/react-query';
import { notifySuccess } from '@/portainer/services/notifications';

import { Registry, RegistryTypes } from '../../types/registry';
import { queryKeys as registriesQueryKeys } from '../../queries/query-keys';

import { deleteManifest, getTagManifestV2 } from './manifest.service';
import { getRepositoryTags } from './useRepositoryTags';

interface RemoveRepositoryWithQueueParams {
  registry: Registry;
  repository: string;
  endpointId?: number;
  onStateChange: (state: QueueState) => void;
}

// Helper to delete ECR repository
async function deleteECRRepository(
  registry: Registry,
  repository: string,
  endpointId?: number
) {
  const encodedRepository = encodeURIComponent(repository);
  await axios.delete(
    `/registries/${registry.Id}/ecr/repositories/${encodedRepository}`,
    { params: { endpointId } }
  );
}

async function removeRepositoryWithQueue({
  registry,
  repository,
  endpointId,
  onStateChange,
}: RemoveRepositoryWithQueueParams) {
  // Handle ECR separately (simpler case)
  if (registry.Type === RegistryTypes.ECR) {
    const startTime = Date.now();
    onStateChange({
      totalCount: 1,
      completedCount: 0,
      status: 'PROCESSING',
      elapsedTime: 0,
    });

    await deleteECRRepository(registry, repository, endpointId);

    onStateChange({
      totalCount: 1,
      completedCount: 1,
      status: 'SUCCESS',
      elapsedTime: Math.round((Date.now() - startTime) / 1000),
    });
    return;
  }

  // Non-ECR: Multi-phase operation
  // Phase 1: Load all repository tag names
  const tagNamesResponse = await getRepositoryTags({
    registryId: registry.Id,
    repository,
    environmentId: endpointId,
  });

  const tagNames = tagNamesResponse?.tags || [];

  // Track cumulative elapsed time across sequential operations
  let manifestLoadingElapsedTime = 0;

  // Phase 2: Load all tag manifests
  // Dynamically update total as we discover unique digests
  const { uniqueDigests } = await loadTagManifests(
    registry,
    repository,
    tagNames,
    endpointId,
    (state, uniqueDigestCount) => {
      manifestLoadingElapsedTime = state.elapsedTime;
      // Total operations = tags to load + unique digests to delete
      const dynamicTotal = tagNames.length + uniqueDigestCount;
      onStateChange({
        totalCount: dynamicTotal,
        completedCount: state.completedCount,
        status: state.status,
        elapsedTime: state.elapsedTime,
      });
    }
  );

  // Phase 3: Delete all unique manifests
  const finalTotal = tagNames.length + uniqueDigests.length;

  await deleteAllManifests(
    registry,
    repository,
    uniqueDigests,
    endpointId,
    (state) => {
      // Add tag count to deletion progress for total progress
      onStateChange({
        totalCount: finalTotal,
        completedCount: tagNames.length + state.completedCount,
        status: state.status,
        elapsedTime: manifestLoadingElapsedTime + state.elapsedTime,
      });
    }
  );
}

async function loadTagManifests(
  registry: Registry,
  repository: string,
  tagNames: string[],
  endpointId: number | undefined,
  onProgress: (state: QueueState, uniqueDigestCount: number) => void
): Promise<{ shortTags: RepositoryShortTag[]; uniqueDigests: string[] }> {
  const shortTags: RepositoryShortTag[] = [];
  const uniqueDigests = new Set<string>();

  const queue = new RequestQueue<RepositoryShortTag | null>({
    maxConcurrent: findBestStep(tagNames.length),
    onStateChange: (state) => onProgress(state, uniqueDigests.size),
  });

  tagNames.forEach((tagName) => {
    queue.push(async (): Promise<RepositoryShortTag | null> => {
      try {
        const v2 = await getTagManifestV2({
          id: registry.Id,
          endpointId,
          repository,
          tag: tagName,
        });
        const shortTag = new RepositoryShortTag(
          tagName,
          v2.config.digest,
          v2.digest,
          v2
        );
        shortTags.push(shortTag);
        // Track unique digests as we discover them
        if (shortTag.ImageDigest) {
          uniqueDigests.add(shortTag.ImageDigest);
        }
        return shortTag;
      } catch (error) {
        return null; // Skip failed tags
      }
    });
  });

  await queue.getResults();
  return { shortTags, uniqueDigests: Array.from(uniqueDigests) };
}

async function deleteAllManifests(
  registry: Registry,
  repository: string,
  digests: string[],
  endpointId: number | undefined,
  onProgress: (state: QueueState) => void
): Promise<void> {
  const queue = new RequestQueue<void>({
    maxConcurrent: Math.min(findBestStep(digests.length), digests.length),
    onStateChange: onProgress,
  });

  digests.forEach((digest) => {
    queue.push(async () => {
      await deleteManifest({
        id: registry.Id,
        repository,
        tag: digest,
        endpointId,
      });
    });
  });

  await queue.getResults();
}

export function useRemoveRepository() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, RemoveRepositoryWithQueueParams>(
    removeRepositoryWithQueue,
    {
      onSuccess: (_, removeRepositoryVars) => {
        notifySuccess('Success', 'Repository successfully removed');
        queryClient.invalidateQueries(
          registriesQueryKeys.repositoriesList(removeRepositoryVars.registry.Id)
        );
      },
      ...withError('Failed to remove repository'),
    }
  );
}
